import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Checkbox, Input } from '@ibsys/kit';
import type { DashboardBlockId, DashboardQualityDisplaySettings, HiddenCriterionRef } from '@/entities/dashboards';
import type { QualityZoneRanges } from '@/shared/lib';
import { CriteriaVisibilityPicker } from './CriteriaVisibilityPicker';

/**
 * Фиксированный порядок блоков дашборда в списке настроек отображения — совпадает с порядком
 * секций на странице `QualityDashboardPage`.
 */
const DASHBOARD_BLOCK_IDS: DashboardBlockId[] = [
  'kpiRow',
  'ratingsCharts',
  'distributionRow',
  'topCallsRow',
  'analyticsRow',
  'heatmap',
  'consultantReport',
  'allDetails',
];

type Props = {
  /** Текущие пороги зон (нижние границы доли балла от максимума для зелёной и жёлтой зон). */
  zoneRanges: QualityZoneRanges;
  /** Критерии, скрытые из отображения, — каждый в разрезе своего заключения. */
  hiddenCriteria: HiddenCriterionRef[];
  /** Идентификаторы скрытых блоков дашборда. */
  hiddenBlocks: DashboardBlockId[];
  /** Колбэк изменения настроек отображения — принимает полный новый объект. */
  onChange: (next: DashboardQualityDisplaySettings) => void;
};

/** Доля балла (0..1) → целый процент для показа в числовом поле. */
const toPercent = (ratio: number): number => Math.round(ratio * 100);

/** Ограничивает процент диапазоном 0..100. */
const clampPercent = (pct: number): number => Math.min(100, Math.max(0, pct));

/**
 * Панель «Настройки отображения» дашборда качества — сворачиваемая область в панели фильтров,
 * по умолчанию свёрнутая и визуально отделённая от фильтров отбора данных.
 *
 * Управляет тем, *как* показан дашборд (а не *какие данные* отобраны): границы зон качества
 * мини-отчёта, набор скрытых критериев и набор скрытых блоков. Все изменения отдаются наверх
 * полным объектом настроек отображения; персистом и сбросом управляет страница.
 */
export const DisplaySettings: FC<Props> = ({ zoneRanges, hiddenCriteria, hiddenBlocks, onChange }) => {
  const { t } = useTranslation('qualityDashboard');

  // Каждое изменение отдаём полным объектом: стор настроек мержит `display` целиком.
  const emit = (patch: Partial<DashboardQualityDisplaySettings>) =>
    onChange({ zoneRanges, hiddenCriteria, hiddenBlocks, ...patch });

  // Границы зон храним долями, показываем процентами. Границы независимы: сдвиг одной не
  // трогает другую, поэтому промежуточный ввод (например «8» на пути к «80») не разрушает
  // соседнюю границу. Пустое поле игнорируем — иначе `Number('')` сбросил бы границу в 0.
  const handleChangeGreen = (raw: string) => {
    if (raw.trim() === '') return;
    const pct = Number(raw);
    if (!Number.isFinite(pct)) return;
    emit({ zoneRanges: { green: clampPercent(pct) / 100, yellow: zoneRanges.yellow } });
  };

  const handleChangeYellow = (raw: string) => {
    if (raw.trim() === '') return;
    const pct = Number(raw);
    if (!Number.isFinite(pct)) return;
    emit({ zoneRanges: { green: zoneRanges.green, yellow: clampPercent(pct) / 100 } });
  };

  const handleToggleBlock = (id: DashboardBlockId, visible: boolean) => {
    const next = visible ? hiddenBlocks.filter((b) => b !== id) : [...hiddenBlocks, id];
    emit({ hiddenBlocks: next });
  };

  return (
    <Accordion type="single" collapsible className="border-t pt-2">
      <AccordionItem value="display" className="border-0">
        <AccordionTrigger className="font-medium">{t('displaySettings.title')}</AccordionTrigger>
        <AccordionContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
            {/* Границы зон качества */}
            <div className="flex flex-col gap-3">
              <h4 className="text-sm font-medium">{t('displaySettings.zoneRanges')}</h4>
              <Input
                type="number"
                size="sm"
                label={t('displaySettings.greenThreshold')}
                value={toPercent(zoneRanges.green)}
                onChange={(e) => handleChangeGreen(e.target.value)}
              />
              <Input
                type="number"
                size="sm"
                label={t('displaySettings.yellowThreshold')}
                value={toPercent(zoneRanges.yellow)}
                onChange={(e) => handleChangeYellow(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">{t('displaySettings.zoneRangesHint')}</p>
            </div>

            {/* Показ критериев — полный каталог всех отчётов, сгруппированный по отчётам */}
            <div className="flex flex-col gap-3">
              <h4 className="text-sm font-medium">{t('displaySettings.criteria')}</h4>
              <div className="max-h-48 overflow-auto">
                <CriteriaVisibilityPicker
                  hiddenCriteria={hiddenCriteria}
                  onChange={(next) => emit({ hiddenCriteria: next })}
                />
              </div>
            </div>

            {/* Показ блоков дашборда */}
            <div className="flex flex-col gap-3">
              <h4 className="text-sm font-medium">{t('displaySettings.blocks')}</h4>
              <div className="flex flex-col gap-2 max-h-48 overflow-auto">
                {DASHBOARD_BLOCK_IDS.map((id) => (
                  <Checkbox
                    key={id}
                    label={t(`displaySettings.blockNames.${id}`)}
                    value={!hiddenBlocks.includes(id)}
                    onChange={(value) => handleToggleBlock(id, value === true)}
                  />
                ))}
              </div>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
